import type { OpportunityRelatedDeal, OpportunityRelatedInclusion } from '~/hooks/apis/offers/useGetOpportunityRelated';

import { getStatusLabel, isFiniteNumber } from '../../shared/checklistUtils';

import type { DealOptionsInclusionsChecklistRow, DealOptionsInclusionsDealRow } from './types';

export function buildDealOptionsInclusionsRows({
  dealDataAvailable,
  relatedDeals,
  relatedInclusions,
  relatedInclusionsAvailable,
}: {
  dealDataAvailable: boolean;
  relatedDeals: Array<OpportunityRelatedDeal>;
  relatedInclusions: Array<OpportunityRelatedInclusion>;
  relatedInclusionsAvailable: boolean;
}): Array<DealOptionsInclusionsChecklistRow> {
  const rows: Array<DealOptionsInclusionsChecklistRow> = [];

  const inclusionsByDealSfid = new Map<string, Array<OpportunityRelatedInclusion>>();
  for (const inc of relatedInclusions) {
    const key = inc.deal_offer__c;
    if (!key) {
      continue;
    }
    const existing = inclusionsByDealSfid.get(key) ?? [];
    existing.push(inc);
    inclusionsByDealSfid.set(key, existing);
  }

  for (const deal of relatedDeals) {
    const dealKey = deal.sfid ?? String(deal.id);
    const roomName = deal.room_asset?.name ?? '—';

    if (!dealDataAvailable) {
      rows.push({
        dealId: dealKey,
        dealOptionName: deal.name ?? '',
        description: '',
        id: `blocked:${dealKey}`,
        inclusionAssetValue: null,
        dealOptionInclusionRrp: null,
        name: deal.name ?? '',
        rrpMatch: false,
        roomName,
        status: 'blocked',
        statusLabel: getStatusLabel('blocked'),
        totalValue: null,
        type: '',
      });
      continue;
    }

    if (!relatedInclusionsAvailable) {
      rows.push({
        dealId: dealKey,
        dealOptionName: deal.name ?? '',
        description: '',
        id: `blocked-inc:${dealKey}`,
        inclusionAssetValue: null,
        dealOptionInclusionRrp: null,
        name: deal.name ?? '',
        rrpMatch: false,
        roomName,
        status: 'blocked',
        statusLabel: getStatusLabel('blocked'),
        totalValue: null,
        type: '',
      });
      continue;
    }

    for (const inc of inclusionsByDealSfid.get(dealKey) ?? []) {
      const rrp = isFiniteNumber(inc.rrp__c) ? inc.rrp__c : null;
      const assetValue = isFiniteNumber(inc.total_value__c) ? inc.total_value__c : null;
      const rrpMatch = rrp !== null && assetValue !== null && rrp === assetValue;

      rows.push({
        dealId: dealKey,
        dealOptionName: deal.name ?? '',
        description: inc.description__c ?? '',
        id: `inc:${dealKey}:${inc.id}`,
        inclusionAssetValue: assetValue,
        dealOptionInclusionRrp: rrp,
        name: inc.name ?? '',
        rrpMatch,
        roomName,
        status: rrpMatch ? 'approved' : 'needsApproval',
        statusLabel: getStatusLabel(rrpMatch ? 'approved' : 'needsApproval'),
        totalValue: assetValue,
        type: inc.type__c ?? '',
      });
    }
  }

  return rows;
}

export function buildDealOptionsInclusionsDealRows({
  flatRows,
  relatedDeals,
}: {
  flatRows: Array<DealOptionsInclusionsChecklistRow>;
  relatedDeals: Array<OpportunityRelatedDeal>;
}): Array<DealOptionsInclusionsDealRow> {
  const inclusionsByDealId = new Map<string, Array<DealOptionsInclusionsChecklistRow>>();
  for (const row of flatRows) {
    const existing = inclusionsByDealId.get(row.dealId) ?? [];
    existing.push(row);
    inclusionsByDealId.set(row.dealId, existing);
  }

  return relatedDeals.map((deal) => {
    const dealKey = deal.sfid ?? String(deal.id);

    return {
      id: dealKey,
      costPrice: deal.cost_price__c ?? null,
      currency: deal.currencyisocode ?? 'AUD',
      dealOptionName: deal.name ?? '',
      inclusions: inclusionsByDealId.get(dealKey) ?? [],
      marginPercent: deal.margin_percent__c ?? null,
      rackRate: deal.rack_rate__c ?? null,
      roomName: deal.room_asset?.name ?? '—',
      sellPrice: deal.price__c ?? null,
    };
  });
}

export function getDealOptionsInclusionsSearchText(row: DealOptionsInclusionsChecklistRow) {
  return [row.dealOptionName, row.name, row.description, row.type, row.roomName, row.statusLabel]
    .filter(Boolean)
    .join(' ');
}
